import { fmtRating } from "@/lib/api";

const avg = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);
const yesCount = (top, k) => top.filter((c) => c[k]).length;
const photos = (n) => (n == null ? "?" : n >= 10 ? "10+" : String(n));
const yn = (b) => (b ? "oui" : "non");

// Only a trade category (architecte, architecte d'intérieur...) is shown; Google's generic values ("Services") are hidden.
export const isTrade = (cat) => !!cat && /archi|int[ée]rieur|d[ée]cor|design|paysag|urban|ma[iî]tre d|bureau d|ing[ée]ni|construct|r[ée]nov/i.test(cat);

function commonCategory(top) {
  const n = {};
  top.forEach((c) => isTrade(c.category) && (n[c.category] = (n[c.category] || 0) + 1));
  const best = Object.entries(n).sort((a, b) => b[1] - a[1])[0];
  return best && best[1] >= 2 ? best : null;
}

const allOrTwo = (k, n) => (k === n ? `Les ${n} premiers` : `${k} des ${n} premiers`);

// Most striking observed fact, fixed priority: site, photos, reviews, hours, category.
export function readingSentence(top, u) {
  const n = top.length;
  if (!n) return null;
  if (!u) {
    if (yesCount(top, "has_site") === n) return `Les ${n} premiers ont tous leur site relié à leur fiche.`;
    if (top.every((c) => (c.photos || 0) >= 10)) return `Les ${n} premiers ont tous au moins 10 photos sur leur fiche.`;
    return null;
  }
  const s = yesCount(top, "has_site");
  if (!u.has_site && s >= 2) return s === n ? `Les ${n} premiers ont tous leur site relié à leur fiche. Toi non.` : `${allOrTwo(s, n)} ont leur site relié à leur fiche. Toi non.`;
  if (top.every((c) => (c.photos || 0) >= 10) && (u.photos ?? 10) < 10) return `Les ${n} premiers ont tous au moins 10 photos sur leur fiche. Toi ${u.photos}.`;
  const a = Math.round(avg(top.map((c) => c.reviews || 0)));
  if (a >= 2 * Math.max(1, u.reviews || 0) && a >= 10)
    return u.reviews ? `Le top 3 a en moyenne ${a} avis, soit ${Math.floor(a / u.reviews)} fois plus que toi.` : `Le top 3 a en moyenne ${a} avis. Toi aucun.`;
  const h = yesCount(top, "has_hours");
  if (!u.has_hours && h >= 2) return `${allOrTwo(h, n)} affichent leurs horaires. Toi non.`;
  const cc = commonCategory(top);
  if (cc && isTrade(u.category) && u.category !== cc[0]) return `${allOrTwo(cc[1], n)} sont en catégorie « ${cc[0]} ». Toi « ${u.category} ».`;
  return null;
}

const ROWS = [
  { k: "reviews", label: "Avis Google", short: "Avis", v: (c) => String(c.reviews ?? 0), weak: (u, t) => (u.reviews || 0) < avg(t.map((c) => c.reviews || 0)) },
  { k: "rating", label: "Note", short: "Note", v: (c) => fmtRating(c.rating), weak: (u, t) => (u.rating || 0) < avg(t.map((c) => c.rating || 0)) },
  { k: "photos", label: "Photos sur la fiche", short: "Photos", v: (c) => photos(c.photos), weak: (u, t) => (u.photos || 0) < avg(t.map((c) => c.photos || 0)) },
  { k: "has_site", label: "Site relié", short: "Site", v: (c) => yn(c.has_site), weak: (u, t) => !u.has_site && yesCount(t, "has_site") >= 2 },
  { k: "has_hours", label: "Horaires renseignés", short: "Horaires", v: (c) => yn(c.has_hours), weak: (u, t) => !u.has_hours && yesCount(t, "has_hours") >= 2 },
  { k: "category", label: "Catégorie principale", short: "Catégorie", v: (c) => (isTrade(c.category) ? c.category : "–"), weak: (u, t) => { const cc = commonCategory(t); return !!cc && u.category !== cc[0]; } },
];

const Dot = () => <span className="ml-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--warning)] align-middle" data-testid="weak-dot" aria-label="plus faible que le top 3" />;

export default function Podium({ data, onPlace, bare = false }) {
  const top = data.top3 || [];
  const u = data.user?.status === "found" ? data.user : null;
  const sentence = readingSentence(top, u);
  const vol = data.demand?.volume;
  const rows = ROWS.filter((r) => r.k !== "category" || [...top, u].some((c) => c && isTrade(c.category)));
  return (
    <section data-testid="zone-1" className={bare ? "" : "fade-up rounded-card border border-line bg-surface px-4 py-5 sm:px-5"} aria-label="Zone 1 · Le podium">
      {!bare && (
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-ink">Zone 1 · Le podium</h2>
        <span className="rounded-full border border-line bg-surface-2 px-2.5 py-0.5 text-[12px] font-medium text-ink" data-testid="zone-1-pill">
          Débloquée
        </span>
      </div>
      )}
      {vol != null && (
        <p className="mt-3 text-[19px] font-semibold leading-snug tracking-[-0.01em] text-ink sm:text-[21px]" data-testid="demand-line">
          Chaque mois, environ <span className="num">{vol.toLocaleString("fr-FR")}</span> recherches « {data.demand.keyword} » sur Google.
        </p>
      )}
      <table className="mt-4 w-full table-fixed border-collapse text-[13px]" data-testid="podium-table">
        <colgroup>
          <col className="w-[22%] sm:w-[28%]" />
          <col />
          <col />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr className="text-left text-[12px] text-muted">
            <th className="pb-2 font-normal" />
            {top.map((c) => (
              <th key={c.rank} className="num pb-2 pr-1 font-medium text-ink">#{c.rank}</th>
            ))}
            <th className="pb-2 font-semibold text-ink" data-testid="podium-toi">Toi</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.k} className="border-t border-line align-top" data-testid={`podium-row-${r.k}`}>
              <th scope="row" className="py-2 pr-1 text-left text-[12px] font-normal leading-tight text-muted">
                <span className="sm:hidden">{r.short}</span>
                <span className="hidden sm:inline">{r.label}</span>
              </th>
              {top.map((c) => (
                <td key={c.rank} className={`py-2 pr-1 leading-tight text-ink ${r.k === "category" ? "hyphens-auto text-[11px] sm:text-[12px]" : "num"}`}>
                  {r.v(c)}
                </td>
              ))}
              <td className={`py-2 leading-tight text-ink ${r.k === "category" ? "hyphens-auto text-[11px] sm:text-[12px]" : "num"}`} data-testid={`podium-toi-${r.k}`}>
                {u ? (
                  <>
                    {r.v(u)}
                    {r.weak(u, top) && <Dot />}
                  </>
                ) : (
                  <span className="text-muted">?</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!u && (
        <button type="button" onClick={onPlace} data-testid="podium-place-link" className="mt-2 text-[13px] font-medium text-ink underline underline-offset-4">
          Place ton agence
        </button>
      )}
      {sentence && (
        <p className="mt-3 text-[14px] leading-relaxed text-ink" data-testid="podium-sentence">
          {sentence}
        </p>
      )}
      <p className="mt-3 text-[12px] text-muted" data-testid="google-mention">
        Données Google.
      </p>
    </section>
  );
}
